import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UsersRound, CalendarClock, ClipboardList, AtSign, ArrowRight, BookText } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { api } from "@/lib/api";
import { fmtRange } from "@/data/officeOptions";

const Row = ({ testid, to, icon: Icon, label, value, alert }) => (
  <Link
    to={to}
    data-testid={testid}
    className="-mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-[#F5F8FF]"
  >
    <span className="flex items-center gap-2 text-sm font-semibold text-[#14245F]"><Icon size={15} className="shrink-0 text-[#1D4ED8]" /> {label}</span>
    <span className={`flex items-center gap-1.5 text-right text-sm ${alert ? "font-bold text-[#C2410C]" : "text-[#5A6B8C]"}`}>{value} <ArrowRight size={13} className="shrink-0 text-[#9DAAC6]" /></span>
  </Link>
);

const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const sessLabel = (s) => (s ? `${fmtDate(s.date)} · ${fmtRange(s.start_time, s.end_time)}${s.location ? ` · ${s.location}` : ""}` : "None scheduled");
const workTo = (w, person) => (w?.id ? `/office?tab=schedule&entry=${w.id}` : `/office?tab=schedule&person=${person}`);

export const OfficeTeamCard = ({ refreshKey }) => {
  const [ov, setOv] = useState(null);
  const [wr, setWr] = useState(null);
  const [nb, setNb] = useState(null);
  useEffect(() => {
    api.officeWeekOverview().then(setOv).catch(() => setOv(null));
    api.weeklyReviewCurrent().then(setWr).catch(() => setWr(null));
    api.notebookSummary().then(setNb).catch(() => setNb(null));
  }, [refreshKey]);

  const unread = nb?.unread_mentions || 0;
  const weeklyValue = wr?.current ? `This week · ${wr.current.status}` : (wr ? `Next · ${wr.meeting_day}, ${wr.meeting_time}` : "—");

  return (
    <SectionCard
      testid="cc-office-team-card"
      accent="#1D4ED8"
      icon={UsersRound}
      eyebrow="Office & Team"
      title="Office & Team"
      subtitle="Where the founders plan, meet, work and think together"
      action={<Link to="/office?tab=schedule" data-testid="cc-office-team-open" className="inline-flex items-center gap-1 rounded-full bg-[#1D4ED8]/10 px-3 py-1 text-xs font-semibold text-[#1D4ED8] transition-colors hover:bg-[#1D4ED8]/15">Open <ArrowRight size={13} /></Link>}
    >
      <div className="divide-y divide-[#EEF2FB]">
        <Row testid="cc-ot-next-session" to="/office?tab=schedule" icon={CalendarClock} label="Next shared office session" value={sessLabel(ov?.next_session)} />
        <Row testid="cc-ot-steven" to={workTo(ov?.steven_next_work, "Steven")} icon={CalendarClock} label="Steven’s next scheduled work" value={sessLabel(ov?.steven_next_work)} />
        <Row testid="cc-ot-richard" to={workTo(ov?.richard_next_work, "Richard")} icon={CalendarClock} label="Richard’s next scheduled work" value={sessLabel(ov?.richard_next_work)} />
        <Row testid="cc-ot-weekly" to="/office?tab=weekly-review" icon={ClipboardList} label="Next Weekly Review" value={weeklyValue} />
        <Row testid="cc-ot-notebook" to="/office?tab=notebook&view=mentions" icon={AtSign} label="Founders Notebook alerts" value={unread > 0 ? `${unread} unread` : "No alerts"} alert={unread > 0} />
        <Row testid="cc-ot-diary" to="/office?tab=diary" icon={BookText} label="Add today’s diary entry" value="Quick log" />
      </div>
    </SectionCard>
  );
};
